import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { resolveApiUrl } from '../services/api';

interface RegisterProps {
  onSwitchToLogin: () => void;
  onBack?: () => void;
}

const Register: React.FC<RegisterProps> = ({ onSwitchToLogin, onBack }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGoogleLogin = () => {
    window.location.href = resolveApiUrl(`/api/auth/google/oauth?_=${Date.now()}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      setLoading(false);
      return;
    }
    if (formData.password.length < 6) {
      setError(t('forms.validation.minLength', { count: 6 }));
      setLoading(false);
      return;
    }

    const { confirmPassword, ...registerData } = formData;
    const success = await register(registerData);
    if (!success) setError(t('auth.registerError'));
    setLoading(false);
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        overflowY: 'auto',
        display: 'grid',
        placeItems: 'start center',
        background: 'var(--bg)',
        color: 'var(--ink)',
        padding: '40px 20px',
        WebkitUserSelect: 'text',
        userSelect: 'text',
      }}
    >
      {onBack && (
        <button
          onClick={onBack}
          style={{
            position: 'fixed',
            top: 20,
            left: 20,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            background: 'transparent',
            color: 'var(--ink-muted)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius)',
            fontFamily: 'inherit',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={14} /> {t('common.back')}
        </button>
      )}

      <div
        style={{
          width: '100%',
          maxWidth: 460,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          padding: '36px 34px 30px',
          boxShadow: 'var(--shadow)',
          marginTop: 40,
        }}
      >
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: 12,
            background: 'var(--accent)',
            color: 'var(--accent-contrast)',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: '-0.5px',
            marginBottom: 20,
          }}
        >
          N
        </div>

        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 30,
            fontWeight: 500,
            letterSpacing: '-0.8px',
            color: 'var(--ink)',
            marginBottom: 6,
          }}
        >
          {t('auth.createAccount')}
        </h1>
        <p style={{ color: 'var(--ink-muted)', fontSize: 14, marginBottom: 24 }}>
          {t('auth.alreadyHaveAccount')}{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--accent)',
              fontWeight: 600,
              fontFamily: 'inherit',
              fontSize: 14,
              padding: 0,
              cursor: 'pointer',
            }}
          >
            {t('auth.signIn')}
          </button>
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="mobile-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label htmlFor="firstName">{t('clients.firstName')}</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                value={formData.firstName}
                onChange={handleChange}
                placeholder={t('clients.firstName')}
              />
            </div>
            <div>
              <label htmlFor="lastName">{t('clients.lastName')}</label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                value={formData.lastName}
                onChange={handleChange}
                placeholder={t('clients.lastName')}
              />
            </div>
          </div>

          <div>
            <label htmlFor="username">{t('auth.username')}</label>
            <input
              id="username"
              name="username"
              type="text"
              required
              value={formData.username}
              onChange={handleChange}
              placeholder={t('auth.username')}
            />
          </div>

          <div>
            <label htmlFor="email">{t('common.email')}</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="phone">{t('common.phone')}</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+55 11 99999-9999"
            />
          </div>

          <div>
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              style={{
                display: 'flex',
                gap: 10,
                background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
                color: 'var(--danger)',
                borderRadius: 'var(--radius)',
                padding: '10px 12px',
                fontSize: 12,
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>{error}</div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: 4,
              padding: '11px 14px',
              background: 'var(--accent)',
              color: 'var(--accent-contrast)',
              border: 'none',
              borderRadius: 'var(--radius)',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? `${t('auth.creatingAccount')}…` : t('auth.createAccount')}
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 14px' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
          <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>{t('common.or')}</span>
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
        </div>
        <button
          type="button"
          onClick={handleGoogleLogin}
          style={{
            width: '100%',
            padding: '11px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--ink)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path
              fill="#4285F4"
              d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"
            />
            <path
              fill="#34A853"
              d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"
            />
            <path
              fill="#FBBC05"
              d="M4.5 10.49a4.8 4.8 0 0 1 0-3.07V5.35H1.83a8 8 0 0 0 0 7.28l2.67-2.07v-.07z"
            />
            <path
              fill="#EA4335"
              d="M8.98 4.72c1.16 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.36L4.5 7.42a4.77 4.77 0 0 1 4.48-2.7z"
            />
          </svg>
          {t('auth.loginWithGoogle')}
        </button>
      </div>
    </div>
  );
};

export default Register;
